import React, { memo, useState, useCallback } from 'react';
import { includes } from 'rambda';
import {
    IconBuildShape,
    IconCaretRight,
    IconFolder,
    IconFolderMask,
    IconHideSmall,
    IconImage,
    IconImageMask,
    IconMaskAlt3,
    IconShowSmall,
    IconTextField,
    IconTextMask,
} from '@bynder/icons';
import { Thumbnail } from '@bynder/design-system';
import { ElementTypes } from '@bynder-studio/render-core';
import Editable from '../../../editable/Editable';
import { elementSortFn } from '../../helpers/elementtree';
import { EditorIcon, EditorIconLarge, LayersListItem } from './Layer.styled';

const generateIcon = (layerElement: any) => {
    let icon = null;

    const isElementMasked = layerElement?.properties?.mask !== null;

    if (layerElement?.type === ElementTypes.TEXT) {
        icon = isElementMasked ? <IconTextMask /> : <IconTextField />;
    }

    if (layerElement?.type === ElementTypes.IMAGE) {
        if (isElementMasked) {
            icon = <IconImageMask />;
        } else if (layerElement?.src) {
            icon = (
                // @ts-expect-error TODO(VBS-25739) Thumbnail typing lacks style prop
                <Thumbnail imageUrl={layerElement?.src} style={{ width: '24px', height: '24px', objectFit: 'cover' }} />
            );
        } else {
            icon = <IconImage />;
        }
    }

    if (layerElement?.type === ElementTypes.GROUP) {
        icon = isElementMasked ? <IconFolderMask /> : <IconFolder />;
    }

    if (layerElement?.type === ElementTypes.SHAPE) {
        icon = isElementMasked ? <IconMaskAlt3 /> : <IconBuildShape />;
    }

    return icon;
};

const Layer = memo(function Layer(props: any) {
    const { layer, enableTools, depth, selectedElementIds = [], expandedGroupIds } = props;
    const { toggleElementVisibility, onSelect = () => {}, toggleGroupExpand, changeElementName } = props;
    const { name, type, id, disabled } = layer;
    const { hidden } = layer.properties;

    const [editable, setEditable] = useState(false);

    const toggleEditing = useCallback((val: any) => {
        setEditable(val);
    }, []);

    const changeName = useCallback(
        (newName: any) => {
            changeElementName(id, newName);
            setEditable(false);
        },
        [changeElementName, id],
    );

    const active = selectedElementIds.includes(layer.id);
    const isGroup = type === 'GROUP';
    const isExpanded = isGroup && includes(layer.id, expandedGroupIds);

    const style: any = {
        paddingLeft: 5 + depth * 25,
        pointerEvents: layer?.children?.length ? 'all' : 'none',
    };

    const toggleExpand = (evt: any, layerId: any) => {
        evt.stopPropagation();
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        isGroup && toggleGroupExpand(layerId);
    };

    const activeClass = active ? 'layers__list-item--active' : '';
    const invalidClass = layer.invalid ? 'layers__list-item--invalid' : '';
    const disabledClass = layer.disabled ? 'layers__list-item--disabled font-italic' : '';

    return (
        <div>
            <LayersListItem
                className={`${activeClass} ${invalidClass} ${disabledClass}`}
                style={style}
                onClick={(e) => {
                    if (!layer.disabled) {
                        onSelect(layer.id);
                        toggleExpand(e, layer.id);
                    }
                }}
            >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                    className="layers__expand-container horizontal-center"
                    onClick={(evt) => toggleExpand(evt, layer.id)}
                >
                    {isGroup && (
                        <div className={`layers__icon-expand ${isExpanded ? 'layers__expanded' : ''}`}>
                            <IconCaretRight />
                        </div>
                    )}
                </div>
                <div className="layers__type-icon">
                    <EditorIcon>{generateIcon(layer)}</EditorIcon>
                </div>
                <div className="layers__element-title text-truncate">
                    {enableTools && !disabled && active ? (
                        <Editable
                            value={name}
                            editable={editable}
                            onToggle={toggleEditing}
                            onConfirm={changeName}
                            confirmBeforeUnmount={true}
                            hideIcon={true}
                            useCapture={true}
                            className="text-truncate"
                        />
                    ) : (
                        name
                    )}
                </div>
                {toggleElementVisibility && (
                    <div className="layers__visibility-toggle">
                        {/* eslint-disable-next-line react/button-has-type */}
                        <button className="btn btn-link text-inherit p-0" onClick={() => toggleElementVisibility(id)}>
                            <EditorIconLarge>{hidden ? <IconHideSmall /> : <IconShowSmall />}</EditorIconLarge>
                        </button>
                    </div>
                )}
            </LayersListItem>
            {isExpanded &&
                Object.values(layer.children)
                    .sort(elementSortFn())
                    .map((child: any) => <Layer {...props} key={child.id} depth={depth + 1} layer={child} />)}
        </div>
    );
});

export default Layer;
